import type { Disposable, DisposeFn } from "./types";

/**
 * A utility class for managing disposal of resources. Dispose must be manually
 * called. If a disposal function is added after dispose has been called, it is
 * called immediately.
 */
export class Disposer implements Disposable {
  private _disposed = false;
  private disposeFns: Array<DisposeFn> = [];

  /**
   * Returns whether this has been disposed.
   */
  get disposed() {
    return this._disposed;
  }

  /**
   * Adds a function to be called when this is disposed. If already disposed,
   * the function is called immediately.
   */
  add(fn: DisposeFn) {
    if (this.disposed) {
      this.safeRun(fn);
    } else {
      this.disposeFns.push(fn);
    }
  }

  /**
   * Disposes all registered functions and marks this as disposed.
   */
  dispose = () => {
    if (this.disposed) {
      return;
    }
    const { disposeFns } = this;
    this._disposed = true;
    this.disposeFns = [];
    disposeFns.reverse().forEach((fn) => this.safeRun(fn));
  };

  private safeRun(fn: DisposeFn) {
    try {
      fn();
    } catch (e) {
      // Swallow any errors to ensure all dispose functions are run.
      if (process.env.NODE_ENV === "development") {
        console.trace("Unexpected error in dispose:", e);
      }
    }
  }
}
